import { Box, Button, Divider, Grid, Popover, Typography, useTheme } from '@mui/material';
import React, { useState, MouseEvent } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import WSTextField from '../TextBox/WSTextBox';
import WSSelectField from '../Select/WSSelect';
import WSDateField from '../DateField/WSDateField';
import WSMultiCheckBox from '../CheckBox/WSMultiCheckBox';
import { IOptions } from '../Lib/Lib';
import WSRadioField from '../Radio/WSRadio';
import { IsSmallScreen } from '../../commonFunctions/CommonFunctions';
import FilterListIcon from '@mui/icons-material/FilterList'
import SaveIcon from '@mui/icons-material/Save'
import ResetIcon from '@mui/icons-material/Restore'
import { Tune } from '@mui/icons-material';

interface IFilterProps {
  rows: IRows[];
  onFilterChange: (filters: any) => void;
  alignment?: 'left' | 'center' | 'right';
  headerType?: "simple" | "HaderTopCornered";
  defaultFilter?: any[];
}

export interface IRows {
  type: "text" | "checkbox" | "date" | "dropdown" | "radio";
  label: string;
  fieldName: string;
  options?: IOptions[];
  range?: boolean;
  operator?: Operator;
  align?: 'horizontal' | 'vertical';
  rowDivision?: 6 | 12;
  showOperator?: boolean;
}

export interface IFilter {
  field: string;
  operator: Operator | string;
  value: string | number;
}

export type Operator =
  | "eq"
  | "ne"
  | "in"
  | "nin"
  | "regex"
  | "gte"
  | "lte"
  | "gt"
  | "like"
  | "lt";


export const WSFilterComponent: React.FC<IFilterProps> = React.memo(({ rows, onFilterChange, alignment = 'left', headerType = "simple", defaultFilter }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const methods = useForm();
  const theme = useTheme();
  const { register, handleSubmit, control, reset, formState: { } } = methods;
  const isMobile = IsSmallScreen();

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    reset();
  };

  const handleReset = () => {

    const emptyValues = rows.reduce((acc, row) => {
      acc[row.fieldName] = ''; // or [] for multi-select/checkbox fields
      return acc;
    }, {} as Record<string, any>);

    // Reset the form with empty values
    reset(emptyValues);

    // Clear filters
    onFilterChange(defaultFilter || []);
    reset();
  };

  const open = Boolean(anchorEl);
  const id = open ? 'filter-popover' : undefined;

  const removeEmptyFilters = (filters: any) => {
    return Object.keys(filters).reduce((acc, key) => {
      const value = filters[key];
      if (value !== null && value !== undefined && value !== '' && !(Array.isArray(value) && value.length === 0)) {
        acc[key] = value;
      }
      return acc;
    }, {} as any);
  };

  const onSubmit = (data: any) => {
    const cleanedFilters = removeEmptyFilters(data);
    const appliedFilters = rows.reduce((acc, column) => {
      if (cleanedFilters[column.fieldName]) {
        acc.push({
          field: column.fieldName,
          operator: column.operator || "eq",
          value: cleanedFilters[column.fieldName],
        });
      }
      return acc;
    }, [] as IFilter[]);
    if (defaultFilter && defaultFilter.length > 0) {
      // const appliedFields = appliedFilters.map(item => item.field); // Extract fields from appliedFilters
      // const commonElements = defaultFilter.filter(item => appliedFields.includes(item.field));
      // if (commonElements?.length === 0) {
      appliedFilters.push(...defaultFilter);
      // }
    }
    onFilterChange(appliedFilters);
    setAnchorEl(null);
  };
  const sxHaderTopCornered: any = {
    backgroundColor: "#A71A68",
    textTransform: "none",
    "&:hover": { backgroundColor: "#b94886" }
  }
  const sxSimple: any = {
    height: 32, width: isMobile ? 20 : undefined,
    backgroundColor: "#A71A68",
    textTransform: "none",
    "&:hover": { backgroundColor: "#b94886" }
  }
  return (
    <>
      <Box
        display="flex"
        justifyContent={alignment}
        sx={{
          height: isMobile ? 33 : 32,
          marginTop: isMobile ? -2.2 : undefined
        }}
      >
        <Button
          variant="contained"
          color="primary"
          onClick={handleClick}
          startIcon={<Tune />}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            padding: '8px 16px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            '&:hover': {
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              backgroundColor: theme.palette.primary.dark,
            }
          }}
        >
          Filters
        </Button>
      </Box>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        style={{ maxWidth: 1000, marginTop: 5 }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        className="popover-main"
      >
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)} className="popover-content">
            <Grid container alignItems="center" style={{ padding: 12, marginLeft: 10 }}>
              <Typography style={{ fontWeight: 'bold', color: theme.palette.primary.dark, fontSize: '1rem' }}>
                Filter Options
              </Typography>
            </Grid>
            <Divider style={{ marginLeft: 10, }} />

            <Grid item style={{ margin: 10 }}>
              <Grid container spacing={1}>
                {rows.map(({ type, fieldName, label, options, align, rowDivision }) => (
                  <Grid item sm={rowDivision ? rowDivision : 12} xs={12} key={fieldName}>
                    {type === 'text' && (
                      <WSTextField
                        control={control}
                        id={fieldName}
                        {...register(fieldName)}
                        name={fieldName}
                        label={label}
                      />
                    )}
                    {type === 'dropdown' && (
                      <WSSelectField
                        options={options}
                        {...register(fieldName)}
                        id={fieldName}
                        control={control}
                        isMobile={isMobile}
                        label={label}
                        name={fieldName}
                      />
                    )}
                    {type === 'date' && (
                      <WSDateField
                        id={fieldName}
                        name={fieldName}
                        label={label}
                        dateFormat="D/MM/YYYY"
                        control={control}
                        defaultValue={null}
                        rules={{ required: false }}
                      />
                    )}
                    {type === 'checkbox' && (
                      <WSMultiCheckBox
                        id={fieldName}
                        label={label}
                        name={fieldName}
                        options={options || []}
                        align={align}
                        defaultValue={[]}
                        rules={{ required: false }}
                      />
                    )}
                    {type === 'radio' && (
                      <WSRadioField
                        id={fieldName}
                        label={label}
                        name={fieldName}
                        align='Horizontal'
                        options={options || []}
                        defaultValue={[]}
                        rules={{ required: false }}
                        control={control}
                      />
                    )}
                  </Grid>
                ))}
              </Grid>

              <Box>
                <br />
                <Divider />
              </Box>

              <Grid container justifyContent="flex-end" spacing={1} style={{ paddingTop: 20 }}>
                <Grid item sm={2} xs={6} style={{ marginRight: !isMobile ? 20 : undefined }} >
                  <Button
                    type='submit'
                    aria-describedby={id}
                    startIcon={
                      <SaveIcon sx={{
                        color: theme.palette.primary.dark,
                      }} />
                    }
                    sx={{
                      height: '36px',
                      px: 2,
                      fontSize: '0.75rem',
                      fontWeight: 'bold',
                      textTransform: 'none',
                      background: 'white',
                      border: '2px solid transparent',
                      borderRadius: '8px',
                      backgroundImage: 'linear-gradient(white, white), linear-gradient(to right, #27d07c, #16c4e4)',
                      backgroundOrigin: 'border-box',
                      backgroundClip: 'padding-box, border-box',
                      color: 'transparent',
                      position: 'relative',
                      transition: 'all 0.3s ease',
                      marginRight: 2,
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: '10px',
                        right: '10px',
                        bottom: '10px',
                        left: '8px',
                        zIndex: -1,
                        background: 'linear-gradient(to right, #27d07c, #16c4e4)',
                        borderRadius: '20px',
                        opacity: 0,
                        transition: 'opacity 0.3s ease',
                      },
                      '&:hover': {
                        '&::before': {
                          opacity: 0.2,
                        },
                        boxShadow: '0 0 8px rgba(39, 208, 124, 0.5)',
                      },
                      '& .MuiButton-startIcon': {
                        marginRight: '6px',
                      },
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        backgroundImage: 'linear-gradient(to right, #27d07c, #16c4e4)',
                        WebkitBackgroundClip: 'text',
                        backgroundClip: 'text',
                        color: theme.palette.primary.dark
                      }}
                    >
                      Apply
                    </Box>
                  </Button>
                </Grid>
                <Grid item sm={2} xs={6} >
                  <Button
                    aria-describedby={id}
                    onClick={handleReset}
                    startIcon={
                      <ResetIcon sx={{
                        color: theme.palette.primary.dark,
                      }} />
                    }
                    sx={{
                      height: '36px',
                      px: 2,
                      fontSize: '0.75rem',
                      fontWeight: 'bold',
                      textTransform: 'none',
                      background: 'white',
                      border: '2px solid transparent',
                      borderRadius: '8px',
                      backgroundImage: 'linear-gradient(white, white), linear-gradient(to right, #27d07c, #16c4e4)',
                      backgroundOrigin: 'border-box',
                      backgroundClip: 'padding-box, border-box',
                      color: 'transparent',
                      position: 'relative',
                      transition: 'all 0.3s ease',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: '10px',
                        right: '10px',
                        bottom: '10px',
                        left: '8px',
                        zIndex: -1,
                        background: 'linear-gradient(to right, #27d07c, #16c4e4)',
                        borderRadius: '20px',
                        opacity: 0,
                        transition: 'opacity 0.3s ease',
                      },
                      '&:hover': {
                        '&::before': {
                          opacity: 0.2,
                        },
                        boxShadow: '0 0 8px rgba(39, 208, 124, 0.5)',
                      },
                      '& .MuiButton-startIcon': {
                        marginRight: '6px',
                      },
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        backgroundImage: 'linear-gradient(to right, #27d07c, #16c4e4)',
                        WebkitBackgroundClip: 'text',
                        backgroundClip: 'text',
                        color: theme.palette.primary.dark
                      }}
                    >
                      Reset
                    </Box>
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </form>
        </FormProvider>
      </Popover>
    </>
  );
});
